import React, { useState, useEffect } from 'react';
import { campaignsAPI, googleAdsAPI } from '../utils/api';
import { NavLink } from 'react-router-dom';
import GoogleAdsScript from './GoogleAdsScript';

const TABS = ['Website Setup', 'Google Ads Integration', 'Google Ads Script'];

const SCHEDULE_OPTIONS = [
  { value: 'disabled', label: 'Disabled — manual sync only' },
  { value: 'hourly', label: 'Every hour' },
  { value: 'every_4h', label: 'Every 4 hours' },
  { value: 'every_12h', label: 'Every 12 hours' },
  { value: 'daily', label: 'Once a day' },
  { value: 'weekly', label: 'Once a week' },
];

function fmtRelative(iso) {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const API_BASE = process.env.REACT_APP_API_URL || 'https://clicksaver.app';

export default function GoogleAdsSync({ campaignId }) {
  const [activeTab, setActiveTab] = useState('Website Setup');

  if (!campaignId) {
    return (
      <div>
        <div className="page-header"><h1>Setup</h1><p>Configure website tracking and Google Ads sync</p></div>
        <div className="empty">
          <h3>No campaign selected</h3>
          <p>Select a campaign from the sidebar to view setup instructions.</p>
          <NavLink to="/campaigns" className="btn btn-primary" style={{ marginTop: 12, display: 'inline-block' }}>Go to Campaigns →</NavLink>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Setup</h1>
        <p>Website tracking and Google Ads sync configuration</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            background: 'none', border: 'none', padding: '10px 20px', fontSize: 13, fontWeight: 500,
            cursor: 'pointer', whiteSpace: 'nowrap',
            color: activeTab === tab ? 'var(--accent-blue)' : 'var(--text-secondary)',
            borderBottom: `2px solid ${activeTab === tab ? 'var(--accent-blue)' : 'transparent'}`,
            marginBottom: -1,
          }}>{tab}</button>
        ))}
      </div>

      {activeTab === 'Website Setup' && <WebsiteSetupSection campaignId={campaignId} />}
      {activeTab === 'Google Ads Integration' && <GoogleAdsIntegrationSection campaignId={campaignId} />}
      {activeTab === 'Google Ads Script' && <GoogleAdsScript />}
    </div>
  );
}

/* ── Website Setup Section ── */
function WebsiteSetupSection({ campaignId }) {
  const [campaign, setCampaign] = useState(null);
  const [copiedScript, setCopiedScript] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  useEffect(() => {
    if (campaignId) campaignsAPI.get(campaignId).then(setCampaign).catch(() => {});
  }, [campaignId]);

  const tid = campaign?.tracking_id || '';
  const trackingScript = tid ? `<!-- ClickSaver Tracking Script -->
<script>
(function() {
    var tid = '${tid}';
    var api = '${API_BASE}';
    function p(n) { return new URLSearchParams(window.location.search).get(n) || ''; }
    var gclid = p('gclid'), fbclid = p('fbclid');
    if (gclid || fbclid) {
        var img = new Image();
        img.src = api + '/api/clicks/pixel/' + tid
            + '?gclid=' + encodeURIComponent(gclid)
            + '&fbclid=' + encodeURIComponent(fbclid)
            + '&page=' + encodeURIComponent(window.location.href)
            + '&ref=' + encodeURIComponent(document.referrer);
    }
})();
</script>` : '';

  const testUrl = tid ? `${API_BASE}/api/clicks/pixel/${tid}?gclid=test123&page=test` : '';

  const copy = (text, setter) => {
    navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  const monoBox = { background: '#0d1117', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 18px', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 12, color: '#e6edf3', whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.7 };

  return (
    <>
      {/* How it works */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="section-title">How Website Tracking Works</div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {[
            { step: '01', title: 'Visitor Clicks Your Ad', desc: 'Google adds a gclid parameter to your landing page URL when someone clicks your ad' },
            { step: '02', title: 'Script Fires', desc: 'The ClickSaver script on your page detects the gclid and sends a tracking ping to our server' },
            { step: '03', title: 'Fraud Analysis', desc: 'ClickSaver analyses the IP, device, location, and behaviour in real-time' },
            { step: '04', title: 'IP Blocked', desc: 'Fraudulent IPs are blocked and pushed to Google Ads exclusions via the hourly script' },
          ].map(s => (
            <div key={s.step} style={{ flex: '1 1 180px' }}>
              <div style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 11, color: 'var(--accent-blue)', marginBottom: 6 }}>STEP {s.step}</div>
              <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13, color: 'var(--text-primary)' }}>{s.title}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tracking script */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <div className="section-title" style={{ marginBottom: 2 }}>Tracking Script</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Paste this before the <code style={{ background: 'var(--bg-secondary)', padding: '1px 6px', borderRadius: 4, fontSize: 11 }}>&lt;/body&gt;</code> tag on every landing page linked to this campaign
            </div>
          </div>
          <button className="btn btn-ghost" onClick={() => copy(trackingScript, setCopiedScript)} disabled={!tid} style={{ padding: '6px 16px', fontSize: 12, flexShrink: 0 }}>
            {copiedScript ? 'Copied!' : 'Copy Script'}
          </button>
        </div>
        {tid ? (
          <div style={monoBox}>{trackingScript}</div>
        ) : (
          <div style={{ ...monoBox, color: 'var(--text-muted)', textAlign: 'center', padding: 24 }}>Loading campaign...</div>
        )}
        <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(88,166,255,0.06)', border: '1px solid rgba(88,166,255,0.15)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--text-primary)' }}>Only fires on ad clicks</strong> — the script checks for a <code style={{ background: 'var(--bg-secondary)', padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>gclid</code> (Google) or <code style={{ background: 'var(--bg-secondary)', padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>fbclid</code> (Meta) parameter before sending any data. Organic visitors are never tracked.
        </div>
      </div>

      {/* Install guide */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="section-title">Installation Guide</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[
            {
              title: 'WordPress / Elementor',
              desc: 'Go to Appearance → Theme Editor → footer.php, or use a plugin like "Insert Headers and Footers". Paste the script just before </body>.',
              tag: 'WordPress',
            },
            {
              title: 'Shopify',
              desc: 'Go to Online Store → Themes → Edit Code → theme.liquid. Paste the script just before </body>.',
              tag: 'Shopify',
            },
            {
              title: 'HTML / Static Site',
              desc: 'Open your landing page HTML file and paste the script just before the closing </body> tag.',
              tag: 'HTML',
            },
            {
              title: 'Google Tag Manager',
              desc: 'Create a new Custom HTML tag, paste the script inside, and set the trigger to "All Pages". Publish the container.',
              tag: 'GTM',
            },
            {
              title: 'Webflow',
              desc: 'Go to Project Settings → Custom Code → Footer Code. Paste the script there and publish your site.',
              tag: 'Webflow',
            },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', paddingBottom: 16, borderBottom: i < 4 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ flexShrink: 0, padding: '3px 10px', background: 'rgba(88,166,255,0.1)', border: '1px solid rgba(88,166,255,0.2)', borderRadius: 6, fontSize: 11, fontWeight: 700, color: 'var(--accent-blue)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>{item.tag}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{item.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Test / Verify */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="section-title">Verify Installation</div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
          After installing the script, open your landing page URL with <code style={{ background: 'var(--bg-secondary)', padding: '1px 6px', borderRadius: 4, fontSize: 11 }}>?gclid=test123</code> appended. You should see a new click appear in your <NavLink to="/clicks" style={{ color: 'var(--accent-blue)' }}>Click Log</NavLink> within seconds.
        </p>
        {tid && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, padding: '9px 14px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {testUrl}
            </div>
            <button className="btn btn-ghost" onClick={() => copy(testUrl, setCopiedUrl)} style={{ padding: '6px 14px', fontSize: 12, flexShrink: 0 }}>
              {copiedUrl ? 'Copied!' : 'Copy Test URL'}
            </button>
          </div>
        )}
        <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px', padding: '12px 14px', background: 'rgba(63,185,80,0.06)', border: '1px solid rgba(63,185,80,0.15)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600, color: 'var(--accent-green)', marginBottom: 4 }}>✓ Working</div>
            A new entry appears in Click Log with the test IP and <code style={{ fontSize: 11 }}>gclid=test123</code>
          </div>
          <div style={{ flex: '1 1 200px', padding: '12px 14px', background: 'rgba(248,81,73,0.06)', border: '1px solid rgba(248,81,73,0.15)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600, color: 'var(--accent-red)', marginBottom: 4 }}>✗ Not working</div>
            No entry appears — check the script is placed before <code style={{ fontSize: 11 }}>&lt;/body&gt;</code> and the page is published
          </div>
        </div>
      </div>

      {/* Campaign tracking ID info */}
      {tid && (
        <div className="card" style={{ maxWidth: 480 }}>
          <div className="section-title">Campaign Tracking ID</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ flex: 1, padding: '9px 14px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 13, color: 'var(--accent-blue)', letterSpacing: 1 }}>
              {tid}
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
            Unique to this campaign — each campaign has its own tracking ID. If you create multiple campaigns, install a different script on each landing page.
          </div>
        </div>
      )}
    </>
  );
}

/* ── Google Ads Integration Section ── */
function GoogleAdsIntegrationSection({ campaignId }) {
  const [googleStatus, setGoogleStatus] = useState(null);
  const [schedule, setSchedule] = useState('daily');
  const [lastAutoSync, setLastAutoSync] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleSaved, setScheduleSaved] = useState(false);
  const [rollingWindow, setRollingWindow] = useState(60);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const [myCampaigns, setMyCampaigns] = useState([]);
  const [gadsCampaigns, setGadsCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [gadsCampaignId, setGadsCampaignId] = useState('');
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkSaved, setLinkSaved] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [customerIdSaving, setCustomerIdSaving] = useState(false);
  const [customerIdSaved, setCustomerIdSaved] = useState(false);
  const [oauthBanner, setOauthBanner] = useState(null); // { type, ...data }
  const [showGuide, setShowGuide] = useState(false);
  const [liveExclusions, setLiveExclusions] = useState(null);
  const [exclusionsLoading, setExclusionsLoading] = useState(false);
  const [exclusionsError, setExclusionsError] = useState('');
  const [exclusionsFetchedAt, setExclusionsFetchedAt] = useState(null);
  const [exclusionsFilter, setExclusionsFilter] = useState('');
  const [exclusionsExpanded, setExclusionsExpanded] = useState(false);

  const loadExclusions = async () => {
    if (!campaignId) return;
    setExclusionsLoading(true);
    setExclusionsError('');
    try {
      const r = await googleAdsAPI.getExclusions(campaignId);
      setLiveExclusions(Array.isArray(r?.excluded_ips) ? r.excluded_ips : []);
      setExclusionsFetchedAt(new Date().toISOString());
      if (r?.error) setExclusionsError(r.error);
    } catch (e) {
      setExclusionsError(e?.response?.data?.detail || e?.message || 'Failed to load exclusions');
    }
    setExclusionsLoading(false);
  };

  useEffect(() => {
    // Handle OAuth redirect params
    const params = new URLSearchParams(window.location.search);
    const result = params.get('google');
    if (result === 'success') {
      setOauthBanner({ type: 'success', email: params.get('email') || '', cid: params.get('cid') || '' });
      window.history.replaceState({}, '', window.location.pathname);
      if (campaignId) googleAdsAPI.getAuthStatus(campaignId).then(s => {
        setGoogleStatus(s);
        setSelectedAccountId(s?.customer_id || '');
        if (s?.connected) {
          setAccountsLoading(true);
          googleAdsAPI.listAccounts(campaignId)
            .then(list => setAccounts(Array.isArray(list) ? list : []))
            .catch(() => setAccounts([]))
            .finally(() => setAccountsLoading(false));
        }
      }).catch(() => {});
    } else if (result === 'email_mismatch') {
      setOauthBanner({ type: 'email_mismatch', registered: params.get('registered') || '', connected: params.get('connected') || '' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (result === 'no_ads_access') {
      setOauthBanner({ type: 'no_ads_access', email: params.get('email') || '' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (result === 'error') {
      setOauthBanner({ type: 'error', msg: params.get('msg') || 'An unexpected error occurred.' });
      window.history.replaceState({}, '', window.location.pathname);
    }
    googleAdsAPI.getSchedule(campaignId).then(d => {
      setSchedule(d.schedule || 'disabled');
      setLastAutoSync(d.last_auto_sync || null);
      setRollingWindow(d.rolling_window_days || 60);
    }).catch(() => {});
  }, []);

  // Reload per-campaign data whenever active campaign changes
  useEffect(() => {
    if (!campaignId) return;
    setLinkSaved(false);
    setLinkError('');
    setGadsCampaignId('');
    setGadsCampaigns([]);
    setSelectedCampaign(campaignId);
    googleAdsAPI.getAuthStatus(campaignId).then(s => {
      setGoogleStatus(s);
      setSelectedAccountId(s?.customer_id || '');
      setShowGuide(!s?.connected);
      if (s?.connected) {
        setAccountsLoading(true);
        googleAdsAPI.listAccounts(campaignId)
          .then(list => setAccounts(Array.isArray(list) ? list : []))
          .catch(() => setAccounts([]))
          .finally(() => setAccountsLoading(false));
      } else {
        setAccounts([]);
      }
    }).catch(() => {});
    googleAdsAPI.getSchedule(campaignId).then(d => {
      setSchedule(d.schedule || 'disabled');
      setLastAutoSync(d.last_auto_sync || null);
      setRollingWindow(d.rolling_window_days || 60);
    }).catch(() => {});
    loadExclusions();
    googleAdsAPI.listCampaigns(campaignId).then(d => setGadsCampaigns(Array.isArray(d) ? d : [])).catch(err => {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to load campaigns';
      setLinkError(msg);
    });
    campaignsAPI.list().then(list => {
      setMyCampaigns(list || []);
      const active = (list || []).find(c => c.id === campaignId);
      setGadsCampaignId(active?.google_ads_campaign_id || '');
    }).catch(() => {});
  }, [campaignId]); // eslint-disable-line

  const handleCampaignSelect = (id) => {
    setSelectedCampaign(id);
    setLinkSaved(false);
    setLinkError('');
    const found = myCampaigns.find(c => c.id === id);
    setGadsCampaignId(found?.google_ads_campaign_id || '');
  };

  const handleLinkCampaign = async () => {
    if (!selectedCampaign || !gadsCampaignId.trim()) {
      setLinkError('Select a campaign and enter a Google Ads Campaign ID.');
      return;
    }
    setLinkSaving(true);
    setLinkError('');
    try {
      await googleAdsAPI.linkCampaign(selectedCampaign, { google_ads_campaign_id: gadsCampaignId.trim() });
      setMyCampaigns(prev => prev.map(c => c.id === selectedCampaign ? { ...c, google_ads_campaign_id: gadsCampaignId.trim() } : c));
      setLinkSaved(true);
      setTimeout(() => setLinkSaved(false), 3000);
    } catch (e) {
      setLinkError(e?.response?.data?.detail || 'Failed to link campaign.');
    }
    setLinkSaving(false);
  };

  const handleSelectAccount = async (accountId) => {
    setSelectedAccountId(accountId);
    if (!accountId) return;
    const acct = accounts.find(a => a.id === accountId);
    const login = acct?.parent_mcc_id || accountId;
    setCustomerIdSaving(true);
    try {
      await googleAdsAPI.saveCustomer({
        customer_id: accountId,
        login_customer_id: login,
        campaign_id: campaignId,
      });
      setCustomerIdSaved(true);
      setTimeout(() => setCustomerIdSaved(false), 2500);
      googleAdsAPI.listCampaigns(campaignId).then(d => setGadsCampaigns(Array.isArray(d) ? d : [])).catch(() => {});
    } catch (e) {
      setOauthBanner({ type: 'error', msg: e?.response?.data?.detail || 'Failed to save account.' });
    }
    setCustomerIdSaving(false);
  };

  const handleConnect = async () => {
    setOauthBanner(null);
    try {
      const r = await googleAdsAPI.getAuthUrl(campaignId);
      if (r.auth_url) window.location.href = r.auth_url;
      else setOauthBanner({ type: 'error', msg: r.error || 'Could not generate authorization URL.' });
    } catch (e) {
      setOauthBanner({ type: 'error', msg: e?.response?.data?.detail || 'Failed to start Google Ads authorization.' });
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Ads from this campaign?')) return;
    await googleAdsAPI.disconnect(campaignId);
    setGoogleStatus(s => ({ ...s, connected: false, connected_email: null, customer_id: '' }));
    setGadsCampaigns([]);
    setOauthBanner(null);
    setSelectedAccountId('');
    setAccounts([]);
    setLiveExclusions(null);
    setExclusionsFetchedAt(null);
    setExclusionsError('');
  };

  const handleSaveSchedule = async (val) => {
    setSchedule(val);
    setSavingSchedule(true);
    setSyncResult(null);
    try {
      await googleAdsAPI.updateSchedule(val, campaignId, rollingWindow);
      setScheduleSaved(true);
      setTimeout(() => setScheduleSaved(false), 2500);
    } catch (_) {}
    setSavingSchedule(false);
  };

  const handleSaveWindow = async (days) => {
    setRollingWindow(days);
    setSavingSchedule(true);
    try {
      await googleAdsAPI.updateSchedule(schedule, campaignId, days);
      setScheduleSaved(true);
      setTimeout(() => setScheduleSaved(false), 2500);
    } catch (_) {}
    setSavingSchedule(false);
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await googleAdsAPI.syncNow(campaignId);
      setLastAutoSync(new Date().toISOString());
      if (r.error) {
        setSyncResult({ ok: false, text: r.error });
      } else if ((r.campaigns_checked ?? 0) === 0) {
        setSyncResult({ ok: false, text: 'No campaigns linked to Google Ads. Use the "Link Campaign" section below to connect your campaign first.' });
      } else {
        const already = r.already_excluded || 0;
        const removed = r.ips_removed || 0;
        const reconciled = r.ips_reconciled || 0;
        let text = `Synced ${r.ips_synced} new IP${r.ips_synced !== 1 ? 's' : ''} to Google Ads exclusions.`;
        if (already > 0) text += ` (${already} already excluded)`;
        if (reconciled > 0) text += ` Re-pushed ${reconciled} IP${reconciled !== 1 ? 's' : ''} that were removed in Google Ads.`;
        if (removed > 0) text += ` Removed ${removed} expired IP${removed !== 1 ? 's' : ''} outside the ${rollingWindow}-day window.`;
        setSyncResult({ ok: true, text });
      }
    } catch (e) {
      setSyncResult({ ok: false, text: e?.response?.data?.detail || 'Sync failed' });
    }
    setSyncing(false);
    loadExclusions();
  };

  return (
    <div className="card" style={{ maxWidth: 700, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div className="section-title" style={{ margin: 0 }}>Google Ads Integration</div>
        <button
          onClick={() => setShowGuide(v => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
            fontSize: 12, fontWeight: 600,
            background: showGuide ? 'var(--accent-blue)' : 'rgba(56,139,253,0.1)',
            color: showGuide ? '#fff' : 'var(--accent-blue)',
            border: `1px solid ${showGuide ? 'var(--accent-blue)' : 'rgba(56,139,253,0.3)'}`,
            transition: 'all 0.15s',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M6.5 6C6.5 5.17 7.17 4.5 8 4.5s1.5.67 1.5 1.5c0 .83-.5 1.2-1 1.5S8 8 8 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="8" cy="11" r=".75" fill="currentColor"/>
          </svg>
          {showGuide ? 'Hide Setup Guide' : 'Setup Guide'}
        </button>
      </div>

      {/* Setup Guide */}
      {showGuide && (
        <div style={{ marginBottom: 12, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ background: 'var(--bg-secondary)', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>How to connect Google Ads</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Follow these steps to link your Google Ads account to ClickSaver</div>
          </div>

          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 0 }}>

            {/* Step 1 */}
            <div style={{ display: 'flex', gap: 12, paddingBottom: 16, position: 'relative' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--accent-blue)', color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>1</div>
                <div style={{ width: 1, flex: 1, background: 'var(--border)', marginTop: 6 }} />
              </div>
              <div style={{ paddingBottom: 4 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Before you begin — check your prerequisites</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    { icon: '✓', text: 'You have an active Google Ads account at ads.google.com' },
                    { icon: '✓', text: 'Your ClickSaver login email has Admin or Standard access to that Google Ads account' },
                    { icon: '✓', text: 'You will sign in with the same email used to register on ClickSaver' },
                    { icon: '✓', text: '2-Step Verification is enabled on your Google account (required by Google for API access — enable at google.com/landing/2step)' },
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                      <span style={{ color: 'var(--accent-green)', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{item.icon}</span>
                      {item.text}
                    </div>
                  ))}
                </div>
                {/* Access level table */}
                <div style={{ marginTop: 10, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', fontSize: 11 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ padding: '7px 10px', fontWeight: 600, color: 'var(--text-muted)' }}>Access Level</div>
                    <div style={{ padding: '7px 10px', fontWeight: 600, color: 'var(--text-muted)' }}>Can Connect?</div>
                    <div style={{ padding: '7px 10px', fontWeight: 600, color: 'var(--text-muted)' }}>Can Sync IPs?</div>
                  </div>
                  {[
                    { level: 'Admin', connect: true, sync: true },
                    { level: 'Standard', connect: true, sync: true },
                    { level: 'Read-only', connect: false, sync: false },
                    { level: 'Email-only', connect: false, sync: false },
                  ].map((row, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: i < 3 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ padding: '7px 10px', color: 'var(--text-primary)', fontWeight: 500 }}>{row.level}</div>
                      <div style={{ padding: '7px 10px', color: row.connect ? 'var(--accent-green)' : 'var(--accent-red)', fontWeight: 600 }}>{row.connect ? '✓ Yes' : '✗ No'}</div>
                      <div style={{ padding: '7px 10px', color: row.sync ? 'var(--accent-green)' : 'var(--accent-red)', fontWeight: 600 }}>{row.sync ? '✓ Yes' : '✗ No'}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div style={{ display: 'flex', gap: 12, paddingBottom: 16, position: 'relative' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--accent-blue)', color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>2</div>
                <div style={{ width: 1, flex: 1, background: 'var(--border)', marginTop: 6 }} />
              </div>
              <div style={{ paddingBottom: 4, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Click "Connect Google Ads" and sign in</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 8 }}>
                  Click the button at the bottom of this page. Google will ask you to choose an account — <strong>select the same email you used to register on ClickSaver.</strong>
                </div>
                {/* Mock Google account picker */}
                <div style={{ borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden', maxWidth: 340 }}>
                  <div style={{ background: 'var(--bg-secondary)', padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'none', letterSpacing: 0.5 }}>Google Account Picker — Example</div>
                  <div style={{ padding: '8px' }}>
                    {[
                      { email: 'your-registered@email.com', label: 'Use this one', ok: true },
                      { email: 'personal@gmail.com', label: 'Wrong account', ok: false },
                    ].map((acc, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, background: acc.ok ? 'rgba(63,185,80,0.07)' : 'rgba(248,81,73,0.05)', border: `1px solid ${acc.ok ? 'rgba(63,185,80,0.2)' : 'rgba(248,81,73,0.15)'}`, marginBottom: i === 0 ? 6 : 0 }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: acc.ok ? 'var(--accent-green)' : 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                          {acc.email[0].toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)' }}>{acc.email}</div>
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700, color: acc.ok ? 'var(--accent-green)' : 'var(--accent-red)', whiteSpace: 'nowrap' }}>{acc.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div style={{ display: 'flex', gap: 12, paddingBottom: 16, position: 'relative' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--accent-blue)', color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>3</div>
                <div style={{ width: 1, flex: 1, background: 'var(--border)', marginTop: 6 }} />
              </div>
              <div style={{ paddingBottom: 4, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Pick the Google Ads account to protect</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 8 }}>
                  After connecting, the <strong>Google Ads Account</strong> dropdown lists every account your Google login can access. Pick the one you want to protect — each ClickSaver campaign can target a different Google Ads account. ClickSaver auto-detects the correct manager (MCC) login to use.
                </div>
                {/* Mock account dropdown */}
                <div style={{ borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden', maxWidth: 420 }}>
                  <div style={{ background: 'var(--bg-secondary)', padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'none', letterSpacing: 0.5 }}>Google Ads Account — Example</div>
                  <div style={{ padding: '10px 12px', background: '#fff' }}>
                    {[
                      { label: 'Acme AU (123-456-7890)', chosen: true },
                      { label: 'Acme Manager MCC (987-654-3210) — Manager', chosen: false },
                      { label: 'Acme NZ (555-444-3333)', chosen: false },
                    ].map((row, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, background: row.chosen ? 'rgba(56,139,253,0.08)' : 'transparent', border: `1px solid ${row.chosen ? 'rgba(56,139,253,0.3)' : 'transparent'}`, marginBottom: i < 2 ? 4 : 0 }}>
                        <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${row.chosen ? 'var(--accent-blue)' : 'var(--border)'}`, background: row.chosen ? 'var(--accent-blue)' : 'transparent', flexShrink: 0 }} />
                        <span style={{ fontSize: 11, color: '#202124', fontWeight: row.chosen ? 600 : 400 }}>{row.label}</span>
                        {row.chosen && <span style={{ marginLeft: 'auto', fontSize: 9, fontWeight: 700, color: 'var(--accent-blue)' }}>← PICK ONE</span>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--accent-green)', color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>4</div>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Link a Google Ads campaign</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 8 }}>
                  Scroll down to <strong>Link Campaign to Google Ads</strong> and select the campaign you want to protect. Blocked IPs will be applied as account-level exclusions — protecting all your campaigns automatically.
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['Identifies your account', 'Account-level IP exclusions', 'Protects all campaigns incl. PMax'].map((t, i) => (
                    <span key={i} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 20, background: 'rgba(63,185,80,0.08)', border: '1px solid rgba(63,185,80,0.2)', color: 'var(--accent-green)', fontWeight: 500 }}>{t}</span>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* OAuth result banner */}
      {oauthBanner && (() => {
        const { type, email, registered, connected, cid, msg } = oauthBanner;
        if (type === 'success') return (
          <div style={{ marginBottom: 16, padding: '14px 16px', borderRadius: 8, background: 'rgba(63,185,80,0.08)', border: '1px solid rgba(63,185,80,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="#3fb950" strokeWidth="1.5"/><path d="M5 8l2 2 4-4" stroke="#3fb950" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-green)' }}>Google Ads connected successfully</span>
            </div>
            {email && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 23 }}>Connected as <strong>{email}</strong></div>}
            {cid && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 23, marginTop: 2 }}>Customer ID: <span style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>{cid.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}</span></div>}
          </div>
        );
        if (type === 'email_mismatch') return (
          <div style={{ marginBottom: 16, padding: '14px 16px', borderRadius: 8, background: 'rgba(248,81,73,0.07)', border: '1px solid rgba(248,81,73,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="var(--accent-red)" strokeWidth="1.5"/><path d="M8 5v4M8 10.5v.5" stroke="var(--accent-red)" strokeWidth="1.5" strokeLinecap="round"/></svg>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-red)' }}>Wrong Google account</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 10 }}>
              You signed in with <strong>{connected}</strong>, but your ClickSaver account is registered under <strong>{registered}</strong>.<br/>
              You must connect using the same Google account as your ClickSaver login.
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-secondary)', borderRadius: 6, padding: '8px 12px', marginBottom: 10, lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--text-primary)' }}>How to fix:</strong><br/>
              1. Click <em>Connect Google Ads</em> below<br/>
              2. If Google shows multiple accounts, select <strong>{registered}</strong><br/>
              3. Make sure that account has access to your Google Ads
            </div>
            <button onClick={handleConnect} style={{ fontSize: 12, fontWeight: 600, padding: '7px 16px', borderRadius: 7, background: 'var(--accent-blue)', color: '#fff', border: 'none', cursor: 'pointer' }}>
              Try Again with {registered}
            </button>
          </div>
        );
        if (type === 'no_ads_access') return (
          <div style={{ marginBottom: 16, padding: '14px 16px', borderRadius: 8, background: 'rgba(248,81,73,0.07)', border: '1px solid rgba(248,81,73,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="var(--accent-red)" strokeWidth="1.5"/><path d="M8 5v4M8 10.5v.5" stroke="var(--accent-red)" strokeWidth="1.5" strokeLinecap="round"/></svg>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-red)' }}>No Google Ads access</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 10 }}>
              {email ? <><strong>{email}</strong> is not linked to any Google Ads account.</> : 'The connected account has no Google Ads access.'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-secondary)', borderRadius: 6, padding: '8px 12px', marginBottom: 10, lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--text-primary)' }}>How to fix:</strong><br/>
              1. Make sure you have a Google Ads account at <span style={{ color: 'var(--accent-blue)' }}>ads.google.com</span><br/>
              2. Ensure your email has <strong>Admin</strong> or <strong>Standard</strong> access to that account<br/>
              3. Click <em>Try Again</em> and sign in with the correct account
            </div>
            <button onClick={handleConnect} style={{ fontSize: 12, fontWeight: 600, padding: '7px 16px', borderRadius: 7, background: 'var(--accent-blue)', color: '#fff', border: 'none', cursor: 'pointer' }}>
              Try Again
            </button>
          </div>
        );
        if (type === 'error') return (
          <div style={{ marginBottom: 16, padding: '14px 16px', borderRadius: 8, background: 'rgba(248,81,73,0.07)', border: '1px solid rgba(248,81,73,0.25)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-red)', marginBottom: 4 }}>Connection failed</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>{msg}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-secondary)', borderRadius: 6, padding: '8px 12px', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--text-primary)' }}>Common cause:</strong> The Google account used must have <strong>Admin</strong> or <strong>Standard</strong> access to your Google Ads account. Read-only and Email-only access will not work.
            </div>
          </div>
        );
        return null;
      })()}

      {/* Connection status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border)' }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: googleStatus?.connected ? 'var(--accent-green)' : 'var(--border)', flexShrink: 0 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: googleStatus?.connected ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
            {googleStatus?.connected ? 'Google Ads connected' : 'Not connected'}
          </span>
          {googleStatus?.connected && googleStatus?.connected_email && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{googleStatus.connected_email}</span>
          )}
          {googleStatus?.connected && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>This account must have Admin or Standard access in Google Ads — read-only will not work.</span>
          )}
        </div>
        {googleStatus?.connected && (
          <button onClick={handleDisconnect} className="btn btn-ghost" style={{ padding: '4px 12px', fontSize: 11 }}>Disconnect</button>
        )}
      </div>

      {/* Google Ads account — per campaign */}
      {googleStatus?.connected && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Google Ads Account</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
            Pick the Google Ads account this campaign should push blocked IPs to. Each campaign can target a different account.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={selectedAccountId}
              onChange={e => handleSelectAccount(e.target.value)}
              disabled={accountsLoading || customerIdSaving || accounts.length === 0}
              style={{ flex: 1, fontSize: 13, padding: '9px 12px' }}
            >
              <option value="">
                {accountsLoading ? 'Loading accounts…' : accounts.length === 0 ? 'No accounts found' : 'Select an account…'}
              </option>
              {accounts.map(a => {
                const fmtId = a.id.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
                const label = a.name
                  ? `${a.name} (${fmtId})${a.is_manager ? ' — Manager' : ''}`
                  : `${fmtId}${a.is_manager ? ' — Manager' : ''}`;
                return <option key={a.id} value={a.id}>{label}</option>;
              })}
            </select>
            {customerIdSaving && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Saving…</span>}
            {customerIdSaved && <span style={{ fontSize: 11, color: 'var(--accent-green)' }}>Saved!</span>}
          </div>
        </div>
      )}

      {/* Auto Sync Schedule */}
      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Auto Sync Schedule</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
          Automatically push newly blocked IPs to Google Ads exclusion lists on a schedule.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={schedule} onChange={e => handleSaveSchedule(e.target.value)} disabled={savingSchedule}
            style={{ fontSize: 13, padding: '9px 12px', minWidth: 200, flex: '1 1 200px' }}>
            {SCHEDULE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {scheduleSaved && <span style={{ fontSize: 11, color: 'var(--accent-green)', whiteSpace: 'nowrap' }}>Saved!</span>}
        </div>

        {/* Rolling window */}
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>IP Exclusion Window</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
            Only keep IPs in Google Ads exclusions if they were seen within this window. Older IPs are automatically removed on each sync to keep your list accurate and relevant.
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[30, 60, 90].map(days => (
              <button
                key={days}
                onClick={() => handleSaveWindow(days)}
                disabled={savingSchedule}
                style={{
                  padding: '6px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  background: rollingWindow === days ? 'var(--accent-blue)' : 'var(--bg-secondary)',
                  color: rollingWindow === days ? '#fff' : 'var(--text-secondary)',
                  border: `1px solid ${rollingWindow === days ? 'var(--accent-blue)' : 'var(--border)'}`,
                  transition: 'all 0.15s',
                }}
              >
                {days} days
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Last auto-sync: <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{fmtRelative(lastAutoSync)}</span>
          </div>
          <button className="btn btn-ghost" onClick={handleSyncNow} disabled={syncing || !googleStatus?.connected}
            style={{ padding: '6px 16px', fontSize: 11, borderColor: 'var(--accent-blue)', color: 'var(--accent-blue)', flexShrink: 0 }}>
            {syncing ? 'Syncing...' : 'Sync All Now'}
          </button>
        </div>
        {syncResult && (
          <div style={{
            marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12,
            background: syncResult.ok ? 'rgba(63,185,80,0.08)' : 'rgba(248,81,73,0.08)',
            color: syncResult.ok ? 'var(--accent-green)' : 'var(--accent-red)',
            border: `1px solid ${syncResult.ok ? 'rgba(63,185,80,0.2)' : 'rgba(248,81,73,0.2)'}`,
          }}>
            {syncResult.text}
          </div>
        )}
        {/* Live Google Ads IP exclusion list — only when Google Ads is actually connected */}
        {googleStatus?.connected && (() => {
          const list = liveExclusions || [];
          const filtered = exclusionsFilter
            ? list.filter(ip => ip.includes(exclusionsFilter.trim()))
            : list;
          const visible = exclusionsExpanded ? filtered : filtered.slice(0, 20);
          return (
            <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 8, background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>Live IP exclusions in Google Ads</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {exclusionsLoading ? 'Loading…' : `${list.length} IP${list.length !== 1 ? 's' : ''}`}
                    {exclusionsFetchedAt && !exclusionsLoading && ` · updated ${fmtRelative(exclusionsFetchedAt)}`}
                  </span>
                </div>
                <button onClick={loadExclusions} disabled={exclusionsLoading}
                  style={{ padding: '4px 10px', fontSize: 11, background: 'transparent', color: 'var(--accent-blue)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }}>
                  {exclusionsLoading ? '…' : 'Refresh'}
                </button>
              </div>
              {exclusionsError && (
                <div style={{ fontSize: 11, color: 'var(--accent-red)', marginBottom: 8 }}>{exclusionsError}</div>
              )}
              {list.length > 10 && (
                <input
                  type="text"
                  value={exclusionsFilter}
                  onChange={e => setExclusionsFilter(e.target.value)}
                  placeholder="Filter by IP…"
                  style={{ width: '100%', padding: '6px 10px', marginBottom: 8, fontSize: 12, background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 6 }}
                />
              )}
              {list.length === 0 && !exclusionsLoading && !exclusionsError && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  No IP exclusions live in Google Ads yet. Click "Sync All Now" above to push ClickSaver's blocked IPs.
                </div>
              )}
              {visible.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, fontFamily: 'monospace', fontSize: 11 }}>
                  {visible.map(ip => (
                    <span key={ip} style={{ padding: '2px 8px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-secondary)' }}>{ip}</span>
                  ))}
                </div>
              )}
              {filtered.length > 20 && (
                <button onClick={() => setExclusionsExpanded(v => !v)}
                  style={{ marginTop: 8, padding: '4px 0', fontSize: 11, background: 'transparent', color: 'var(--accent-blue)', border: 'none', cursor: 'pointer' }}>
                  {exclusionsExpanded ? `Show less` : `Show all ${filtered.length}`}
                </button>
              )}
            </div>
          );
        })()}

        <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, fontSize: 11, background: 'rgba(56,139,253,0.07)', border: '1px solid rgba(56,139,253,0.2)', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <span style={{ fontSize: 13, lineHeight: 1 }}>💡</span>
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>Verify synced IPs in Google Ads</div>
            <div style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Go to <span style={{ color: 'var(--accent-blue)', fontWeight: 500 }}>Admin</span> → <span style={{ color: 'var(--accent-blue)', fontWeight: 500 }}>Account settings</span> → <span style={{ color: 'var(--accent-blue)', fontWeight: 500 }}>IP exclusions</span>
            </div>
          </div>
        </div>
      </div>

      {/* Link Campaign to Google Ads */}
      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Link Campaign to Google Ads</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.6 }}>
          Select a Google Ads campaign to identify your account. Blocked IPs are applied as account-level exclusions, protecting all campaigns including Performance Max.
        </div>
        {/* Why link explanation */}
        <div style={{ marginBottom: 14, borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
          <div style={{ background: 'var(--bg-secondary)', padding: '8px 14px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="var(--accent-blue)" strokeWidth="1.5"/><path d="M8 7v4M8 5.5v.5" stroke="var(--accent-blue)" strokeWidth="1.5" strokeLinecap="round"/></svg>
            Why is this required?
          </div>
          <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { icon: '→', text: 'ClickSaver uses the linked campaign to identify which Google Ads account your site belongs to.' },
              { icon: '→', text: 'Blocked IPs are then pushed as account-level exclusions — one list that protects every campaign in your account, including Performance Max.' },
              { icon: '→', text: 'You can select any campaign from the list — it does not matter which one. ClickSaver only uses it to identify your account, not to target that campaign specifically.' },
              { icon: '→', text: 'Without linking, ClickSaver does not know which Google Ads account to sync to, so automatic blocking will not work.' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                <span style={{ color: 'var(--accent-blue)', fontWeight: 700, flexShrink: 0 }}>{item.icon}</span>
                {item.text}
              </div>
            ))}
          </div>
        </div>
        {myCampaigns.length > 1 && (
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12 }}>ClickSaver Campaign</label>
            <select value={selectedCampaign} onChange={e => handleCampaignSelect(e.target.value)}
              style={{ fontSize: 13, padding: '9px 12px' }}>
              <option value="">— Select campaign —</option>
              {myCampaigns.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}
        {myCampaigns.length === 1 && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
            Campaign: <strong>{myCampaigns[0].name}</strong>
          </div>
        )}
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: 12 }}>Google Ads Campaign</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {gadsCampaigns.length > 0 ? (
              <select value={gadsCampaignId} onChange={e => setGadsCampaignId(e.target.value)}
                style={{ flex: 1, fontSize: 13, padding: '9px 12px' }}>
                <option value="">— Select Google Ads campaign —</option>
                {gadsCampaigns.filter(c => c.status === 'ENABLED').map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            ) : (
              <div style={{ flex: 1, padding: '10px 14px', background: linkError ? 'rgba(220,38,38,0.08)' : 'rgba(210,153,34,0.08)', border: `1px solid ${linkError ? 'rgba(220,38,38,0.2)' : 'rgba(210,153,34,0.2)'}`, borderRadius: 8, fontSize: 12, color: linkError ? '#dc2626' : 'var(--accent-orange)' }}>
                {linkError
                  ? `Error: ${linkError}`
                  : !googleStatus?.connected
                    ? 'Connect Google Ads first to load your campaigns.'
                    : !selectedAccountId
                      ? 'Pick a Google Ads account above to load its campaigns.'
                      : 'No enabled campaigns found in the selected account.'}
              </div>
            )}
            {gadsCampaigns.length > 0 && (
              <button className="btn btn-primary" onClick={handleLinkCampaign}
                disabled={linkSaving || !selectedCampaign || !gadsCampaignId.trim()}
                style={{ flexShrink: 0, padding: '8px 18px', fontSize: 12 }}>
                {linkSaved ? 'Linked!' : linkSaving ? 'Saving…' : 'Link'}
              </button>
            )}
          </div>
        </div>
        {linkError && (
          <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(248,81,73,0.08)', border: '1px solid rgba(248,81,73,0.2)', borderRadius: 8, fontSize: 12, color: 'var(--accent-red)' }}>
            {linkError}
          </div>
        )}
        {linkSaved && (
          <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(63,185,80,0.08)', border: '1px solid rgba(63,185,80,0.2)', borderRadius: 8, fontSize: 12, color: 'var(--accent-green)' }}>
            Campaign linked! Blocked IPs will now sync to this Google Ads campaign.
          </div>
        )}
        {myCampaigns.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '10px 0' }}>
            No campaigns found. <a href="/campaigns" style={{ color: 'var(--accent-blue)' }}>Create a campaign first.</a>
          </div>
        )}
      </div>

      {/* Connect / Reconnect button */}
      <div style={{ marginTop: 16 }}>
        {!googleStatus?.app_configured && (
          <div style={{ padding: '10px 14px', background: 'rgba(210,153,34,0.1)', border: '1px solid rgba(210,153,34,0.3)', borderRadius: 8, fontSize: 12, color: 'var(--accent-orange)', marginBottom: 12 }}>
            Google Ads API not configured by admin yet.
          </div>
        )}
        <div style={{ padding: '10px 14px', background: 'rgba(88,166,255,0.07)', border: '1px solid rgba(88,166,255,0.2)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--text-primary)' }}>Important:</strong> The Google account you sign in with must have <strong>Admin</strong> or <strong>Standard</strong> access to your Google Ads account. Read-only and Email-only access levels will not work and will cause the connection to fail.
        </div>
        <button className="btn btn-primary" onClick={handleConnect} disabled={!googleStatus?.app_configured} style={{ width: '100%', justifyContent: 'center' }}>
          {googleStatus?.connected ? 'Reconnect Google Ads' : 'Connect Google Ads'}
        </button>
      </div>
    </div>
  );
}
